// =============================================
// FLASHCARD PAGE MODULE
// Study mode with confidence sorting:
//   ✅ Good (green) — know it well
//   ⚠️ Maybe (yellow) — need to review
//   ❌ No Good (red) — don't know it
// Includes analytics, progress tracking, shuffle
// =============================================

import { FLASHCARDS, FLASHCARD_CATEGORIES } from './flashcard-data.js';
import { syncFlashcardsToUser } from '../user-profile.js';

const CONFIDENCE = { GOOD: 'good', MAYBE: 'maybe', BAD: 'bad' };
const CONF_LABELS = { good: '✅ Got It', maybe: '⚠️ Check Later', bad: '❌ Need Work' };
const CONF_COLORS = { good: '#22c55e', maybe: '#eab308', bad: '#ef4444' };

let state = {
    deck: [],
    index: 0,
    flipped: false,
    filter: 'all',
    confidenceFilter: 'all',
    ratings: {},        // { [cardId]: 'good'|'maybe'|'bad' }
    sessionStats: { seen: 0, good: 0, maybe: 0, bad: 0, startTime: 0 },
    studyHistory: []    // [{ date, seen, good, maybe, bad, duration }]
};

function loadState() {
    try {
        const saved = JSON.parse(localStorage.getItem('carb-flashcard-state') || '{}');
        state.ratings = saved.ratings || {};
        state.studyHistory = saved.studyHistory || [];
    } catch { /* fresh start */ }
}

function saveState() {
    try {
        localStorage.setItem('carb-flashcard-state', JSON.stringify({
            ratings: state.ratings,
            studyHistory: state.studyHistory
        }));
    } catch { /* ignore */ }
}

export function setupFlashcardPage() {
    loadState();
    buildDeck();
    renderFlashcardUI();
    wireEvents();
}

function buildDeck() {
    let cards = [...FLASHCARDS];

    // Category filter
    if (state.filter !== 'all') {
        cards = cards.filter(c => c.category === state.filter);
    }

    // Confidence filter
    if (state.confidenceFilter !== 'all') {
        cards = cards.filter(c => (state.ratings[c.id] || 'unrated') === state.confidenceFilter);
    }

    // Shuffle
    for (let i = cards.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [cards[i], cards[j]] = [cards[j], cards[i]];
    }

    state.deck = cards;
    state.index = 0;
    state.flipped = false;
    state.sessionStats = { seen: 0, good: 0, maybe: 0, bad: 0, startTime: Date.now() };
}

function renderFlashcardUI() {
    const container = document.getElementById('flashcard-page-content');
    if (!container) return;

    const stats = getOverallStats();

    container.innerHTML = `
        <div class="fc-page-layout">
            <!-- Left: Controls & Stats -->
            <div class="fc-sidebar glass-panel">
                <h3>Study Filters</h3>
                <div class="fc-filter-group">
                    <label>Topic</label>
                    <select id="fc-cat-select" class="fc-select">
                        ${Object.entries(FLASHCARD_CATEGORIES).map(([k, v]) =>
                            `<option value="${k}" ${state.filter === k ? 'selected' : ''}>${v}</option>`
                        ).join('')}
                    </select>
                </div>
                <div class="fc-filter-group">
                    <label>Confidence</label>
                    <select id="fc-conf-select" class="fc-select">
                        <option value="all" ${state.confidenceFilter === 'all' ? 'selected' : ''}>All Cards</option>
                        <option value="good" ${state.confidenceFilter === 'good' ? 'selected' : ''}>✅ Got It</option>
                        <option value="maybe" ${state.confidenceFilter === 'maybe' ? 'selected' : ''}>⚠️ Check Later</option>
                        <option value="bad" ${state.confidenceFilter === 'bad' ? 'selected' : ''}>❌ Need Work</option>
                        <option value="unrated" ${state.confidenceFilter === 'unrated' ? 'selected' : ''}>⬜ Unrated</option>
                    </select>
                </div>
                <button id="fc-shuffle-btn" class="fc-action-btn">🔀 Shuffle & Restart</button>

                <div class="fc-stats-box">
                    <h4>Progress Overview</h4>
                    <div class="fc-stat-row">
                        <span class="fc-stat-dot" style="background:${CONF_COLORS.good}"></span>
                        <span>Got It</span>
                        <strong>${stats.good}</strong>
                    </div>
                    <div class="fc-stat-row">
                        <span class="fc-stat-dot" style="background:${CONF_COLORS.maybe}"></span>
                        <span>Check Later</span>
                        <strong>${stats.maybe}</strong>
                    </div>
                    <div class="fc-stat-row">
                        <span class="fc-stat-dot" style="background:${CONF_COLORS.bad}"></span>
                        <span>Need Work</span>
                        <strong>${stats.bad}</strong>
                    </div>
                    <div class="fc-stat-row">
                        <span class="fc-stat-dot" style="background:#64748b"></span>
                        <span>Unrated</span>
                        <strong>${stats.unrated}</strong>
                    </div>
                    <div class="fc-progress-visual">
                        <div class="fc-pv-bar" style="width:${stats.goodPct}%;background:${CONF_COLORS.good}"></div>
                        <div class="fc-pv-bar" style="width:${stats.maybePct}%;background:${CONF_COLORS.maybe}"></div>
                        <div class="fc-pv-bar" style="width:${stats.badPct}%;background:${CONF_COLORS.bad}"></div>
                    </div>
                    <div class="fc-mastery-label">${stats.goodPct}% mastered</div>
                </div>

                <div class="fc-session-info">
                    <h4>This Session</h4>
                    <p>Cards seen: <strong id="fc-session-seen">${state.sessionStats.seen}</strong></p>
                </div>
            </div>

            <!-- Right: Card Area -->
            <div class="fc-main-area">
                ${state.deck.length === 0 ? `
                    <div class="fc-empty-state">
                        <span class="fc-empty-icon">🃏</span>
                        <h3>No cards match your filters</h3>
                        <p>Try changing the topic or confidence filter.</p>
                        <button id="fc-reset-filters" class="fc-action-btn">Reset Filters</button>
                    </div>
                ` : `
                    <div class="fc-counter-bar">
                        <span id="fc-card-num">${state.index + 1}</span> / <span id="fc-card-total">${state.deck.length}</span>
                        <span id="fc-card-category" class="fc-cat-badge">${FLASHCARD_CATEGORIES[state.deck[state.index]?.category] || ''}</span>
                    </div>

                    <div class="fc-card-container" id="fc-card-container">
                        <div class="fc-card ${state.flipped ? 'flipped' : ''}" id="fc-the-card">
                            <div class="fc-card-face fc-card-front">
                                <div class="fc-card-label">TERM</div>
                                <div class="fc-card-text" id="fc-term">${state.deck[state.index]?.term || ''}</div>
                                <div class="fc-tap-hint">Tap to reveal</div>
                            </div>
                            <div class="fc-card-face fc-card-back">
                                <div class="fc-card-label">DEFINITION</div>
                                <div class="fc-card-text" id="fc-def">${state.deck[state.index]?.definition || ''}</div>
                            </div>
                        </div>
                    </div>

                    <div class="fc-confidence-btns" id="fc-conf-btns" style="display:${state.flipped ? 'flex' : 'none'}">
                        <button class="fc-conf-btn fc-conf-good" data-conf="good">✅ Got It</button>
                        <button class="fc-conf-btn fc-conf-maybe" data-conf="maybe">⚠️ Check Later</button>
                        <button class="fc-conf-btn fc-conf-bad" data-conf="bad">❌ Need Work</button>
                    </div>

                    <div class="fc-nav-btns">
                        <button id="fc-prev" class="fc-nav-btn" ${state.index === 0 ? 'disabled' : ''}>← Previous</button>
                        <button id="fc-flip" class="fc-flip-main-btn">${state.flipped ? 'Show Term' : 'Flip Card'}</button>
                        <button id="fc-next" class="fc-nav-btn" ${state.index >= state.deck.length - 1 ? 'disabled' : ''}>Next →</button>
                    </div>

                    <div class="fc-progress-track">
                        <div class="fc-pt-fill" id="fc-pt-fill" style="width:${((state.index + 1) / state.deck.length) * 100}%"></div>
                    </div>

                    <div class="fc-keyboard-hint">
                        ← → arrow keys to navigate &nbsp;|&nbsp; Space to flip &nbsp;|&nbsp; 1/2/3 to rate
                    </div>
                `}
            </div>
        </div>
    `;
}

function wireEvents() {
    document.getElementById('fc-cat-select')?.addEventListener('change', e => {
        state.filter = e.target.value;
        buildDeck();
        renderFlashcardUI();
        wireEvents();
    });

    document.getElementById('fc-conf-select')?.addEventListener('change', e => {
        state.confidenceFilter = e.target.value;
        buildDeck();
        renderFlashcardUI();
        wireEvents();
    });

    document.getElementById('fc-shuffle-btn')?.addEventListener('click', () => {
        buildDeck();
        renderFlashcardUI();
        wireEvents();
    });

    document.getElementById('fc-reset-filters')?.addEventListener('click', () => {
        state.filter = 'all';
        state.confidenceFilter = 'all';
        buildDeck();
        renderFlashcardUI();
        wireEvents();
    });

    document.getElementById('fc-flip')?.addEventListener('click', flipCard);
    document.getElementById('fc-card-container')?.addEventListener('click', flipCard);
    document.getElementById('fc-prev')?.addEventListener('click', () => navigate(-1));
    document.getElementById('fc-next')?.addEventListener('click', () => navigate(1));

    document.querySelectorAll('.fc-conf-btn').forEach(btn => {
        btn.addEventListener('click', () => rateCard(btn.dataset.conf));
    });

    // Keyboard shortcuts
    document.removeEventListener('keydown', handleKey);
    document.addEventListener('keydown', handleKey);
}

function handleKey(e) {
    // Only handle when flashcard page is active
    const page = document.getElementById('page-flashcards');
    if (!page || !page.classList.contains('active')) return;

    if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); flipCard(); }
    if (e.key === 'ArrowRight') navigate(1);
    if (e.key === 'ArrowLeft') navigate(-1);
    if (e.key === '1' && state.flipped) rateCard('good');
    if (e.key === '2' && state.flipped) rateCard('maybe');
    if (e.key === '3' && state.flipped) rateCard('bad');
}

function flipCard() {
    if (state.deck.length === 0) return;
    state.flipped = !state.flipped;

    const card = document.getElementById('fc-the-card');
    const confBtns = document.getElementById('fc-conf-btns');
    const flipBtn = document.getElementById('fc-flip');

    if (card) card.classList.toggle('flipped', state.flipped);
    if (confBtns) confBtns.style.display = state.flipped ? 'flex' : 'none';
    if (flipBtn) flipBtn.textContent = state.flipped ? 'Show Term' : 'Flip Card';

    if (state.flipped) {
        state.sessionStats.seen++;
        const seenEl = document.getElementById('fc-session-seen');
        if (seenEl) seenEl.textContent = state.sessionStats.seen;
    }
}

function navigate(dir) {
    if (state.deck.length === 0) return;
    state.index = Math.max(0, Math.min(state.deck.length - 1, state.index + dir));
    state.flipped = false;
    updateCardDisplay();
}

function updateCardDisplay() {
    const card = state.deck[state.index];
    if (!card) return;

    document.getElementById('fc-term').textContent = card.term;
    document.getElementById('fc-def').textContent = card.definition;
    document.getElementById('fc-card-num').textContent = state.index + 1;
    document.getElementById('fc-card-category').textContent = FLASHCARD_CATEGORIES[card.category] || '';

    const theCard = document.getElementById('fc-the-card');
    if (theCard) theCard.classList.remove('flipped');

    const confBtns = document.getElementById('fc-conf-btns');
    if (confBtns) confBtns.style.display = 'none';

    const flipBtn = document.getElementById('fc-flip');
    if (flipBtn) flipBtn.textContent = 'Flip Card';

    document.getElementById('fc-prev').disabled = state.index === 0;
    document.getElementById('fc-next').disabled = state.index >= state.deck.length - 1;
    document.getElementById('fc-pt-fill').style.width = `${((state.index + 1) / state.deck.length) * 100}%`;
}

function rateCard(conf) {
    const card = state.deck[state.index];
    if (!card) return;

    state.ratings[card.id] = conf;
    state.sessionStats[conf]++;
    saveState();
    syncFlashcardsToUser(); // Update global profile

    // Auto-advance to next card
    if (state.index < state.deck.length - 1) {
        state.index++;
        state.flipped = false;
        updateCardDisplay();
    } else {
        // End of deck — save session
        saveSession();
        renderFlashcardUI();
        wireEvents();
    }

    // Update sidebar stats
    updateSidebarStats();
}

function updateSidebarStats() {
    const stats = getOverallStats();
    const box = document.querySelector('.fc-stats-box');
    if (!box) return;

    const rows = box.querySelectorAll('.fc-stat-row strong');
    if (rows[0]) rows[0].textContent = stats.good;
    if (rows[1]) rows[1].textContent = stats.maybe;
    if (rows[2]) rows[2].textContent = stats.bad;
    if (rows[3]) rows[3].textContent = stats.unrated;

    const bars = box.querySelectorAll('.fc-pv-bar');
    if (bars[0]) bars[0].style.width = stats.goodPct + '%';
    if (bars[1]) bars[1].style.width = stats.maybePct + '%';
    if (bars[2]) bars[2].style.width = stats.badPct + '%';

    const label = box.querySelector('.fc-mastery-label');
    if (label) label.textContent = stats.goodPct + '% mastered';
}

function getOverallStats() {
    const total = FLASHCARDS.length;
    let good = 0, maybe = 0, bad = 0;
    Object.values(state.ratings).forEach(r => {
        if (r === 'good') good++;
        else if (r === 'maybe') maybe++;
        else if (r === 'bad') bad++;
    });
    const unrated = total - good - maybe - bad;
    return {
        total, good, maybe, bad, unrated,
        goodPct: Math.round((good / total) * 100),
        maybePct: Math.round((maybe / total) * 100),
        badPct: Math.round((bad / total) * 100)
    };
}

function saveSession() {
    const duration = Math.round((Date.now() - state.sessionStats.startTime) / 1000);
    state.studyHistory.push({
        date: new Date().toISOString(),
        seen: state.sessionStats.seen,
        good: state.sessionStats.good,
        maybe: state.sessionStats.maybe,
        bad: state.sessionStats.bad,
        duration
    });
    saveState();
}
